// Page de détail d'une annonce - Marketplace Particuliers
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, MapPin, Heart, Share2, Phone, MessageSquare,
  Package, Calendar, Eye, ChevronLeft, ChevronRight, Flag,
  Truck, Tag, User, CheckCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const CONDITION_LABELS = {
  neuf: 'Neuf',
  tres_bon_etat: 'Très bon état',
  bon_etat: 'Bon état',
  etat_correct: 'État correct',
  pour_pieces: 'Pour pièces'
};

const CATEGORY_LABELS = {
  materiaux: 'Matériaux',
  outils: 'Outils',
  equipements: 'Équipements',
  surplus_chantier: 'Surplus chantier',
  occasion: 'Occasion'
};

export default function ListingDetailPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('ecosystem_token');

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/listings/${listingId}`);
      if (res.ok) {
        const data = await res.json();
        setListing(data);
      } else {
        toast.error('Annonce non trouvée');
        navigate('/marketplace');
      }
    } catch (e) {
      console.error('Error fetching listing:', e);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleFavorite = async () => {
    if (!token) {
      toast.error('Connecte-toi pour ajouter aux favoris');
      return;
    }

    setIsFavorite(!isFavorite);
    try {
      await fetch(`${API_URL}/api/ecosystem/listings/${listingId}/favorite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
    } catch (e) {
      console.error('Error toggling favorite:', e);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing?.title,
          text: `${listing?.title} - ${formatPrice(listing?.price)}`,
          url: url
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Lien copié !');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Connecte-toi pour envoyer un message');
      navigate('/reseau/connexion');
      return;
    }

    if (!message.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    setSending(true);
    try {
      // TODO: Implement messaging via API
      toast.success('Message envoyé au vendeur !');
      setShowContactForm(false);
      setMessage('');
    } catch (e) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const nextPhoto = () => {
    if (listing?.photos) {
      setCurrentPhoto((prev) => (prev + 1) % listing.photos.length);
    }
  };

  const prevPhoto = () => {
    if (listing?.photos) {
      setCurrentPhoto((prev) => (prev - 1 + listing.photos.length) % listing.photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Package size={60} className="text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Annonce non trouvée</h1>
        <Link to="/marketplace">
          <Button variant="outline">Retour aux annonces</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleFavorite}
              className={`p-2 rounded-full transition-colors ${
                isFavorite ? 'bg-red-100 text-red-500' : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Heart size={22} fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
            >
              <Share2 size={22} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Photos & Details */}
          <div className="lg:col-span-3 space-y-6">
            {/* Photo Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl overflow-hidden shadow-sm"
            >
              <div className="relative aspect-[4/3] bg-gray-100">
                {listing.photos && listing.photos.length > 0 ? (
                  <>
                    <img
                      src={listing.photos[currentPhoto]}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                    
                    {listing.photos.length > 1 && (
                      <>
                        <button
                          onClick={prevPhoto}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button
                          onClick={nextPhoto}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full"
                        >
                          <ChevronRight size={24} />
                        </button>
                        
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {listing.photos.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentPhoto(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentPhoto ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={80} className="text-gray-300" />
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {listing.photos && listing.photos.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {listing.photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhoto(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentPhoto ? 'border-green-500' : 'border-transparent'
                      }`}
                    >
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">
                {listing.description}
              </p>
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Caractéristiques</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Tag size={20} className="text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Catégorie</p>
                    <p className="font-medium text-gray-900">
                      {CATEGORY_LABELS[listing.category] || listing.category}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">État</p>
                    <p className="font-medium text-gray-900">
                      {CONDITION_LABELS[listing.condition] || listing.condition}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin size={20} className="text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Localisation</p>
                    <p className="font-medium text-gray-900">
                      {listing.city} ({listing.postal_code})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck size={20} className={listing.delivery_available ? 'text-green-600' : 'text-gray-400'} />
                  <div>
                    <p className="text-xs text-gray-500">Livraison</p>
                    <p className="font-medium text-gray-900">
                      {listing.delivery_available ? 'Possible' : 'Non disponible'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Price & Contact */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm sticky top-20"
            >
              <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-2">{listing.title}</h1>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-green-600">
                    {formatPrice(listing.price)}
                  </span>
                  {listing.price_negotiable && (
                    <span className="text-sm bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      Négociable
                    </span>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-1">
                  <Eye size={16} />
                  <span>{listing.views_count || 0} vues</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart size={16} />
                  <span>{listing.favorites_count || 0} favoris</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  <span>{formatDate(listing.created_at)}</span>
                </div>
              </div>

              {/* Seller Info */}
              {listing.seller && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Vendeur</h3>
                  <Link
                    to={`/reseau/profil/${listing.seller.username}`}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {listing.seller.profile_photo ? (
                      <img
                        src={listing.seller.profile_photo}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User size={24} className="text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{listing.seller.display_name}</p>
                      <p className="text-sm text-gray-500">@{listing.seller.username}</p>
                    </div>
                  </Link>
                </div>
              )}

              {/* Contact Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => setShowContactForm(true)}
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  data-testid="contact-seller-btn"
                >
                  <MessageSquare size={20} className="mr-2" />
                  Contacter le vendeur
                </Button>

                {listing.seller?.phone && (
                  <a href={`tel:${listing.seller.phone}`} className="block">
                    <Button variant="outline" className="w-full h-12 border-green-200 text-green-700 hover:bg-green-50">
                      <Phone size={20} className="mr-2" />
                      Appeler
                    </Button>
                  </a>
                )}
              </div>

              {/* Report */}
              <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mt-6 mx-auto">
                <Flag size={16} />
                Signaler cette annonce
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Contacter le vendeur</h2>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg mb-4">
              <p className="font-medium text-gray-900">{listing.title}</p>
              <p className="text-green-600 font-semibold">{formatPrice(listing.price)}</p>
            </div>

            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ton message
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Bonjour, je suis intéressé(e) par ton annonce..."
                  rows={4}
                  className="resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <div className="flex gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => setMessage('Bonjour, est-ce que cet article est toujours disponible ?')}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700"
                >
                  Disponible ?
                </button>
                <button
                  type="button"
                  onClick={() => setMessage('Bonjour, quel est ton dernier prix ?')}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700"
                >
                  Prix ?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={sending || !message.trim()}
                data-testid="send-message-btn"
              >
                {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
